import React from 'react'
import _ from 'lodash'

import apis from 'browser/app/models/apis'
import { Entity } from 'shared-libs/models/entity'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { AttachmentViewer } from 'browser/components/atomic-elements/atoms/attachment-viewer/attachment-viewer'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { ErrorBlock } from 'browser/components/atomic-elements/atoms/error-block/error-block'
import { LoadingSpinner, LoadingSpinnerSize } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import './_printable-render.scss'

type Status = 'loading' | 'ready' | 'error'

/** Default path where PrintablePreviewBuilder splices the rendered fileSet into the entity. */
const DEFAULT_ATTACHMENT_PATH = 'document.attachments'

/** How long the updated indicator stays visible, unless the user interacts with the document. */
export const UPDATED_INDICATOR_MS = 10000

/**
 * Renders a printable entity's PDF from its current state, rather than from a stored
 * attachment. Fetches the on-demand preview and hands the resulting fileSet to
 * AttachmentViewer, so the display behaviour stays identical to a stored attachment.
 *
 * @uiComponent
 */
interface IPrintableRenderProps extends IBaseProps {
  /** The printable entity to render. Defaults to the entity from the context. */
  entityId?: string
  entity?: Entity
  loadingText?: string
  errorText?: string
  retryText?: string
  /** Poll interval, ms. Omit to disable. */
  refreshIntervalMs?: number
  /** Entity path the rendered fileSet is read from. Defaults to `document.attachments`. */
  attachmentPath?: string
  carouselProps?: any
}

interface IPrintableRenderState {
  status: Status
  previewEntity: Entity | null
  hasRefreshed: boolean
}

export class PrintableRender extends React.Component<IPrintableRenderProps, IPrintableRenderState> {
  static defaultProps: Partial<IPrintableRenderProps> = {
    loadingText: 'Loading...',
    errorText: 'Failed to load the document.',
    retryText: 'Retry',
  }
  private request: any = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private noticeTimer: ReturnType<typeof setTimeout> | null = null
  private isUnmounted = false

  public state: IPrintableRenderState = {
    status: 'loading',
    previewEntity: null,
    hasRefreshed: false,
  }

  public componentDidMount() {
    this.load()
  }

  public componentDidUpdate(prevProps: IPrintableRenderProps) {
    if (this.resolveEntityId(prevProps) !== this.resolveEntityId(this.props)) {
      this.cancelRequest()
      this.stopPolling()
      this.setState({ previewEntity: null })
      this.load()
      return
    }
    
    if (prevProps.refreshIntervalMs !== this.props.refreshIntervalMs) {
      this.stopPolling()
      if (this.state.previewEntity) {
        this.startPolling()
      }
    }
  }

  public componentWillUnmount() {
    this.isUnmounted = true
    this.stopPolling()
    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer)
    }
    this.cancelRequest()
  }

  public render() {
    const { status, previewEntity } = this.state
    if (status === 'loading') {
      return this.renderPlaceholder(
        <LoadingSpinner size={LoadingSpinnerSize.LG} label={this.props.loadingText} />
      )
    }

    if (status === 'error' || !previewEntity) {
      return this.renderPlaceholder(
        <>
          <ErrorBlock errorText={this.props.errorText} />
          <Button
            className='u-bumperTop'
            buttonText={this.props.retryText}
            onClick={this.handleRetry}
          />
        </>
      )
    }

    const attachments = this.getFileSet(previewEntity)
    return (
      <div className='c-printableRender' onPointerDown={this.dismissUpdatedIndicator}>
        <AttachmentViewer
          {...this.props}
          entity={previewEntity}
          value={attachments}
          carouselProps={{ ...this.props.carouselProps, attachment: attachments }}
        />
        {this.state.hasRefreshed && (
          <span className='c-printableRender-updatedIndicator' data-testid='printableRender-updatedIndicator' />
        )}
      </div>
    )
  }

  private renderPlaceholder(children: React.ReactNode) {
    return <div className='c-printableRender-placeholder'>{children}</div>
  }

  private resolveEntityId(props: IPrintableRenderProps): string | undefined {
    return props.entityId || props.entity?.uniqueId
  }

  private handleRetry = () => this.load()

  private showUpdatedIndicator() {
    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer)
    }
    this.setState({ hasRefreshed: true })
    this.noticeTimer = setTimeout(() => this.dismissUpdatedIndicator(), UPDATED_INDICATOR_MS)
  }

  private dismissUpdatedIndicator = () => {
    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer)
    }
    this.noticeTimer = null
    if (!this.isUnmounted && this.state.hasRefreshed) {
      this.setState({ hasRefreshed: false })
    }
  }

  /** The fileSet the preview endpoint splices in, which AttachmentViewer renders. */
  private getFileSet(entity: Entity | null): any {
    return entity?.get(this.props.attachmentPath ?? DEFAULT_ATTACHMENT_PATH)
  }

  private load(isBackgroundRefresh = false) {
    if (this.request) {
      return
    }

    const entityId = this.resolveEntityId(this.props)
    if (!entityId) {
      this.showError(isBackgroundRefresh)
      return
    }

    if (!isBackgroundRefresh) {
      this.setState({ status: 'loading' })
    }
    const request = apis.getPrintablePreview(entityId)
    this.request = request
    request.then(
      (fetched: Entity) => this.handleFetched(fetched, isBackgroundRefresh),
      () => this.handleFetchFailed(isBackgroundRefresh),
    )
  }

  private handleFetched(fetched: Entity, isBackgroundRefresh: boolean) {
    this.request = null
    if (this.isUnmounted) {
      return
    }

    const document = this.getFileSet(fetched)
    if (!document) {
      this.showError(isBackgroundRefresh)
      return
    }

    const current = this.state.previewEntity
    // Skipped when nothing changed.
    if (isBackgroundRefresh && _.isEqual(document, this.getFileSet(current))) {
      return
    }

    // Polling only starts once a load succeeds, so Retry restarts it after an error.
    this.startPolling()

    if (!current) {
      this.setState({ status: 'ready', previewEntity: fetched })
      return
    }

    // setContent swaps the content but keeps the object, and both halves matter:
    // AttachmentViewer resets its carousel to page 1 whenever `entity` changes identity,
    // while its preview modal resolves the images off that same object. Replacing the
    // entity loses the viewer's page; holding a stale one shows them the old document.
    current.setContent(fetched.content)
    this.setState({ status: 'ready' })
    this.showUpdatedIndicator()
  }

  private handleFetchFailed(isBackgroundRefresh: boolean) {
    this.request = null
    if (this.isUnmounted) {
      return
    }
    this.showError(isBackgroundRefresh)
  }

  private showError(isBackgroundRefresh: boolean) {
    if (isBackgroundRefresh) {
      return
    }
    // No point polling into an error; Retry starts it again.
    this.stopPolling()
    this.setState({ status: 'error', previewEntity: null })
  }

  private startPolling() {
    const interval = this.props.refreshIntervalMs
    if (this.timer || !interval) {
      return
    }
    this.timer = setInterval(() => this.load(true), interval)
  }

  private cancelRequest() {
    this.request?.cancel?.()
    this.request = null
  }

  private stopPolling() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }
}
